// built-from: /contextual-facilitator-companion v2 impl:contextual-facilitator-companion role:implementation
// Spec 100, owner decision 10: ONE way to open the chat, and it is simply
// there. An activity shows its working content and its conversation together
// as soon as it opens. An existing session is resumed; a new one is created by
// the FIRST MESSAGE, never by navigating.
//
// The seam that makes that possible without a second chat: before a session
// exists the SAME shared dock and the SAME shared capture panel are rendered
// with a not-yet-opened controller. Its composer is live; its first send hands
// the message to `useActivityFacilitation`, which creates the session. The
// real conversation then mounts and `FirstMessageRelay` sends that message
// once through the real chat. Nothing here renders messages, replies, choices
// or a composer of its own — it reuses `SessionCapturePanel` and
// `FacilitatorDock` exactly as the companion does.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import SessionCapturePanel from '../components/SessionCapturePanel';
import { FacilitatorDock } from '../components/FacilitatorDock';
import {
  AttachedFacilitator,
  ContextualFacilitatorCompanion,
  useAttachedFacilitator,
  useAttachedFacilitatorOrNull,
  type AttachedFacilitatorController,
  type FacilitatorAttachment,
} from './ContextualFacilitatorCompanion';
import type { ActivityFirstIntent } from '../hooks/useActivityFacilitation';
import type { ChatController } from '../hooks/sessionTypes';

/** What an activity host hands to the shared conversation: the binding state
 * from `useActivityFacilitation`, with the host's own resolved session id
 * (an activity that already has a session passes it here). */
export interface ActivityFacilitationBinding {
  sessionId: string;
  opening: boolean;
  error: string;
  firstIntent: ActivityFirstIntent | null;
  startWith: (intent: ActivityFirstIntent) => void;
  clearFirstIntent: () => void;
  open: () => void;
}

/** A binding for a screen that was handed a session id directly (the legacy
 * vision route): the conversation is already open, so nothing is deferred. */
export function existingSessionBinding(sessionId: string): ActivityFacilitationBinding {
  return {
    sessionId,
    opening: false,
    error: '',
    firstIntent: null,
    startWith: () => undefined,
    clearFirstIntent: () => undefined,
    open: () => undefined,
  };
}

/** The inert controller a working surface sees before its session exists:
 * nothing captured, nothing pending, no authority to write. Cast once, so a
 * field added to the controller by the shared owner does not have to be
 * restated here as a second, drifting definition. */
const NOT_OPENED = {
  sessionId: '',
  experience: null,
  loading: false,
  error: null,
  openingTurn: false,
  state: { facts: [], drafts: [], sealed: false },
  pendingCount: 0,
  rejection: null,
  completion: null,
  canHighlight: false,
  chat: {},
  execute: async () => false,
  publishFocus: () => undefined,
  activateFactChip: () => undefined,
  complete: async () => false,
} as unknown as AttachedFacilitatorController;

/** The controller for an activity's working content, whether or not its
 * session exists yet. Before the first message there is no attachment, so the
 * surface renders from its own document with nothing captured in this session.
 *
 * The companion exports `useAttachedFacilitatorOrNull` for exactly this: a
 * screen that legitimately renders before its conversation exists reads the
 * context and gets null, rather than a throw wrapped in a try/catch. */
export function useActivityController(): AttachedFacilitatorController {
  return useAttachedFacilitatorOrNull() || NOT_OPENED;
}

/** Sends the queued first message once, through the real chat, after the
 * conversation has opened. Waits a commit so the conversation's own opening
 * turn starts first, then waits for it to settle: the consultant's message
 * follows the greeting instead of racing it.
 *
 * Shared with hosts whose open conversation is not the companion (Event
 * Storming, whose chat is the discovery session hook). */
export function useFirstMessageRelay(
  chat: ChatController & { historySeeded?: boolean },
  intent: ActivityFirstIntent | null,
  onDelivered: () => void,
  ready = true,
) {
  const [armed, setArmed] = useState(false);
  const sent = useRef(false);
  useEffect(() => { setArmed(true); }, []);
  useEffect(() => {
    if (sent.current || !intent || !armed || !ready) return;
    if (chat.historySeeded === false || chat.isLoading || chat.inputBusy) return;
    sent.current = true;
    onDelivered();
    if (intent.kind === 'voice') void chat.startVoiceCall();
    else void chat.handleTextMessage(intent.text, intent.files);
  }, [armed, chat, intent, onDelivered, ready]);
}

function FirstMessageRelay({ intent, onDelivered }: {
  intent: ActivityFirstIntent | null;
  onDelivered: () => void;
}) {
  const controller = useAttachedFacilitator();
  useFirstMessageRelay(controller.chat, intent, onDelivered, Boolean(controller.experience) && !controller.openingTurn);
  return null;
}

/** Wraps an activity's screen so its working content and its conversation see
 * the same attachment. Before the session exists the children still render:
 * the activity is usable and its composer is live. */
export function ActivityAttachment({ facilitation, attachment, children }: {
  facilitation: ActivityFacilitationBinding;
  attachment: FacilitatorAttachment;
  children: ReactNode;
}) {
  if (!facilitation.sessionId) return <>{children}</>;
  return (
    <AttachedFacilitator key={facilitation.sessionId} attachment={attachment}>
      {children}
      <FirstMessageRelay intent={facilitation.firstIntent} onDelivered={facilitation.clearFirstIntent} />
    </AttachedFacilitator>
  );
}

/** The not-yet-opened conversation: the shared dock and the shared capture
 * panel, driven by a controller that creates the session on the first send. */
export function PendingActivityConversation({
  facilitation,
  label,
  inputLabel = 'Ask or add context',
  emptyText = 'Start the conversation.',
  engagementId,
  activityId,
}: {
  facilitation: ActivityFacilitationBinding;
  label?: string;
  inputLabel?: string;
  emptyText?: string;
  engagementId?: string | null;
  activityId?: string;
}) {
  const queued = facilitation.firstIntent;
  // 100 (owner decision 8): the panel shows a turn's files itself, so the
  // queued first message carries them as attachments rather than as text - it
  // reads the same before and after the real conversation mounts.
  const queuedFiles = queued?.kind === 'text' ? queued.files : [];
  const queuedText = queued?.kind === 'text'
    ? queued.text
    : queued?.kind === 'voice' ? 'Starting voice…' : '';
  const waiting = Boolean(queued) && !facilitation.error;
  const controller: ChatController = {
    conversationHistory: queuedText || queuedFiles.length
      ? [{
        id: 'activity-first-message',
        type: 'user',
        text: queuedText,
        timestamp: '',
        ...(queuedFiles.length
          ? {
            attachments: queuedFiles.map((file) => ({
              filename: file.name,
              mime: file.type || '',
              size: file.size,
            })),
          }
          : {}),
      }]
      : [],
    isLoading: waiting,
    inputBusy: waiting,
    callStatus: '',
    handleTextMessage: async (message, files = []) => {
      facilitation.startWith({ kind: 'text', text: message, files });
    },
    startVoiceCall: async () => { facilitation.startWith({ kind: 'voice' }); },
    endVoiceCall: () => undefined,
    importFromDocument: async () => ({ ok: false, error: 'Send a message first to start this conversation.' }),
    // The shell owns the failure state and its retry; the panel does not
    // repeat the same sentence underneath it.
    sessionError: null,
    activities: [],
  };
  return (
    <FacilitatorDock label={label} engagementId={engagementId} activityId={activityId}>
      <SessionCapturePanel
        session={controller}
        className="fshell-rail-panel"
        ariaLabel="Facilitated conversation"
        inputLabel={inputLabel}
        emptyText={emptyText}
        facilitationAttachments
      />
    </FacilitatorDock>
  );
}

/** What an activity gives `ActivityShell` as its conversation: the real
 * companion once the session exists, the not-yet-opened conversation before
 * it. Both are the one dock and the one chat panel. */
export function ActivityConversationPanel({
  facilitation,
  onClose,
  label = 'ArcPilot facilitator',
  inputLabel = 'Ask or add context',
  emptyText = 'Start the conversation.',
  engagementId,
  activityId,
}: {
  facilitation: ActivityFacilitationBinding;
  onClose: () => void;
  label?: string;
  inputLabel?: string;
  emptyText?: string;
  engagementId?: string | null;
  activityId?: string;
}) {
  if (facilitation.sessionId) return <ContextualFacilitatorCompanion onClose={onClose} label={label} />;
  return (
    <PendingActivityConversation
      facilitation={facilitation}
      label={label || 'ArcPilot facilitator'}
      inputLabel={inputLabel}
      emptyText={emptyText}
      engagementId={engagementId}
      activityId={activityId}
    />
  );
}
